import React, { FC, useEffect, useState } from 'react'
import t from 'src/translations/main'
import utils, { checkMode } from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import storage from 'src/components/Core/Utils/storage'
import updateModal from 'src/components/Sections/Modal/updateModal'
import { parseJson } from 'src/views/Pages/Onboarding/FormWrapper'
import {
  useDropdownOptions,
  useShareHolderDetails,
  useShareHolderDetailsUpdate,
} from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Table from 'src/components/Organisms/Table/Table'
import Font from 'src/components/Core/Typography/Font/Font'
import ModalOwnershipDetailsForm from './ModalOwnershipDetails/ModalOwnershipDetailsForm'

import {
  FormStyle,
  FormHeader,
  FormTitle,
  FormDescription,
  FormFooter,
  FormFooterActionsLeft,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import {
  ApiData,
  FormProps,
  TableConfigParam,
  TableObj,
} from '../../../../Pages/Onboarding/types'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import { getBaseApiHost, getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import RadioGroup from 'src/components/Molecules/RadioGroup/RadioGroup'
import { OnSubmitParams } from 'src/components/Core/Hooks/useForm/useForm'
import { Trans } from 'react-i18next'
import Popup from 'src/components/Atoms/Popup/Popup'

const tableConfig: TableConfigParam[] = [
  { key: 'name' },
  { key: 'shareholderDateOfBirth' },
  { key: 'shareholderResidentialAddress' },
  { key: 'ownershipPercentage' },
  { key: 'isUBOOwnershipDirect' },
  { key: 'entityStateName' },
  {
    name: 'pencil',
    color: 'ultraViolet',
  },
  {
    name: 'close',
    color: 'error',
  },
  { key: 'shareholderTown' },
  { key: 'shareholderPostcode' },
  { key: 'shareholderCountry' },
  { key: 'emailID' },
  { key: 'cscDocument' },
]

const tableColumns = [
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col1'
    ),
    accessor: 'col1',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col2'
    ),
    accessor: 'col2',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col3'
    ),
    accessor: 'col3',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col4'
    ),
    accessor: 'col4',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col5'
    ),
    accessor: 'col5',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.CompanyOwnershipForm.table.col6'
    ),
    accessor: 'col6',
  },
  {
    Header: '',
    accessor: 'col7',
  },
  {
    Header: '',
    accessor: 'col8',
  },
]
const HOST: string = getBaseApiHost()
let initialRenderFlag = true

const CompanyOwnershipForm: FC<FormProps> = props => {
  const [genericError, setGenericError] = useState({error: false, message: ''})
  const [formData, setFormData] = useState({
    uboExists: props?.fetchedData?.uboExists || ''
  })
  const [serverValidationError, setServerValidationError] = useState('')
  const [submittedSuccesifully, setSubmittedSuccesifully] = useState(false)

  const tableData = useShareHolderDetails()
  const updateTableData = useShareHolderDetailsUpdate()
  const { dropdownOptions } = useDropdownOptions()

  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)
  /* eslint-disable  @typescript-eslint/no-unused-vars */
  const submitHandler = ({ data, options, configData, setConfigData }: OnSubmitParams) => {
    const modifyResponse = (responseData?: ApiData, response?: Response) => {
      if(props.isLastForm){
        const modifyResponseForSubmit = (responseData?: ApiData) => {
          const serverValidationErrorsParsed = parseJson(responseData?.reasonPhrase)
          if(Array.isArray(serverValidationErrorsParsed) && serverValidationErrorsParsed.length > 0){
            // temp message until getting the error codes from VP
            setServerValidationError('Error on submit')
          } else {
            storage.set(storage?.keys?.onboarding?.applicationStatus,'submitted')
            setSubmittedSuccesifully(true)
            props.nextStep(true)
          }
        }
        sendRequest(
          {
            scope: 'onboarding',
            url: import.meta.env.VITE_MOCK_DATA === 'true'
              ? `${window.location.origin}/mock/postMessage.json`
              : `${getBaseApiHost()}/CorpAOF/submitCorpAOFApplication?applicationid=${applicationId}&step=4`,
            method: 'POST',
            body: { },
          },
          modifyResponseForSubmit
        )
      } else if (!error) {
        props.nextStep()
      }
    }
    if(formData.uboExists === 'false'){
      updateTableData([])
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: `${HOST}/CorpAOF/updateUbos05?applicationId=${applicationId}&UBOexists=${formData.uboExists}`,
        method: 'POST',
        headers: {
          'Content-Type':  options?.contentType || 'application/json',
        },
      },
      modifyResponse
    )

  }

  const submitForm = () => {
    submitHandler({data: formData})
  }

  const { sendRequest, isLoading, error } = useFetch()

  const editShareHolderDetails = (id: number) => {
    updateModal({
      title: t('onboarding.modals.ShareholderDetails.title'),
      modalState: 'open',
      content: (
        <ModalOwnershipDetailsForm
          actionType='edit'
          action={action}
          tableConfig={tableConfig}
          postUrl={getEndpointUrl('CorpAOF/updateshareholderdetails05')}
          id={id}
        />
      ),
      removeFooter: true,
    })
  }

  const deleteShareHolder = (id: number) => {
    // @ts-ignore:next-line
    const modifyResponse = (data: ApiData, response) => {
      if (data && response.ok) {
        updateTableData((prevState: TableObj[]) => {
          return utils.deleteTableRow([...prevState], id)
        })
      }
      setGenericError({error: true, message: t('global.errorInOperation')})
      updateModal({ modalState: 'close' })
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl('CorpAOF/deleteshareholderdetails05', applicationId, 'deleteShareholderDetails.json', `ShareHolderID=${id}`),
        method: 'DELETE',
      },
      modifyResponse,
      () => {
        setGenericError({error: true, message: t('global.errorInOperation')})
        updateModal({ modalState: 'close' })
      }
    )
  }

  const openConfirmDeleteModal = (id: number) => {
    updateModal({
      title: t('onboarding.modals.ShareholderDetails.delete.title'),
      modalState: 'open',
      content: (
        <Font>
          {t('onboarding.modals.ShareholderDetails.delete.content')}
        </Font>
      ),
      footerActions: [
        {
          label: t('components.forms.labels.yes'),
          version: 2,
          onClick: () => {
            deleteShareHolder(id)
          },
        },
        {
          label: t('components.forms.labels.no'),
          onClick: () => {
            updateModal({ modalState: 'close' })
          },
        },
      ],
    })
  }

  const action = (id: number, action: string) => {
    if (action === 'edit') editShareHolderDetails(id)
    if (action === 'delete') openConfirmDeleteModal(id)
  }

  const createTableData = (
    apiData: ApiData,
    tableConfig: TableConfigParam[]
  ) => {
    const newTableData: TableObj[] = []
    apiData.forEach((rowDataObj: ApiData) => {
      const country = dropdownOptions?.countries?.find(country => country?.index.toString() === rowDataObj?.shareholderCountry)?.label
      const canBuildAddress = rowDataObj?.shareholderResidentialAddress && rowDataObj?.shareholderPostcode && rowDataObj?.shareholderTown && country
      // @ts-ignore
      const rowDataObjMapped = {
        ...rowDataObj,
        shareholderCountry: country,
        shareholderResidentialAddress: canBuildAddress ? `${rowDataObj?.shareholderResidentialAddress}, ${rowDataObj?.shareholderPostcode}, ${rowDataObj?.shareholderTown}, ${country?.replace?.(country?.substring?.(0,1),country?.substring?.(0,1).toUpperCase?.()).replace?.(country?.substring?.(1),country?.substring?.(1).toLowerCase?.())}` : rowDataObj?.shareholderResidentialAddress,
      }
      const tableObj = utils.createTableObj(
        rowDataObjMapped,
        tableConfig,
        action
      )
      newTableData.push({...tableObj, col5: rowDataObj?.isUBOOwnershipDirect === 'true' ? 'Yes' : 'No'})
    })

    return newTableData
  }

  useEffect(() => {
    const setFetchedDataHandler = (data: ApiData) => {
      if (data?.getShareholderDetails?.length !== 0) {
        updateTableData(
          createTableData(data.getShareholderDetails, tableConfig)
        )
      }
      initialRenderFlag = false
    }

    if (initialRenderFlag !== true) return

    // remove this sendRequest as soon as the changes are implemented on the backend
    sendRequest({
      scope: 'onboarding',
      url: getEndpointUrl('CorpAOF/getshareholderdetails05', applicationId, 'allShareholderDetails.json'),
    }, setFetchedDataHandler)
    // uncomment this when changes are made in the backend
    // setFetchedDataHandler(props.fetchedData)
  }, [])

  useEffect(() => {
    if(tableData?.length === 0 && !initialRenderFlag){
      if(props.setOptionsList){
        props.setOptionsList(prevList => (
          prevList?.map((obj, index) => {
            if(index === 4) {
              obj.state = 'in_progress'
            }
            return obj
          })
        ))
      }

      if(props.setCompletedStepList){
        props.setCompletedStepList(prevList => (
          prevList?.map((value, index) => {
            return index === 4 ? false : value
          })
        ))
      }
    }

  },[tableData])

  if(initialRenderFlag && isLoading) return <Spinner removeFullscreen />

  return (
    <FormStyle onSubmit={submitForm}>
      <FormHeader>
        <FormTitle>
          {t('onboarding.forms.CorporateAccount.CompanyOwnershipForm.title')}
        </FormTitle>
        <FormDescription>
          {t(
            'onboarding.forms.CorporateAccount.CompanyOwnershipForm.description'
          )}
        </FormDescription>
      </FormHeader>
      <RadioGroup
        name='uboExists'
        label={t('onboarding.forms.CorporateAccount.CompanyOwnershipForm.inputs.uboExists.label')}
        required={true}
        dataList={[
          {
            id: 'regulatorPaymentInstitutionType1',
            label: t('components.forms.labels.yes'),
            value: 'true',
          },
          {
            id: 'regulatorPaymentInstitutionType2',
            label: t('components.forms.labels.no'),
            value: 'false',
          },
        ]}
        onChange={event => {setFormData({
          uboExists: event?.target?.value || ''
        })}}
        value={formData.uboExists}
        disabled={checkMode('readOnly')}
      />
      {
        formData.uboExists === 'true' &&
        <>
          {/* @ts-ignore:next-line */}
          <Font margin='0 0 24px 0'>{<Trans i18nKey={'onboarding.forms.CorporateAccount.CompanyOwnershipForm.visualOnly.pleaseProvide'} />}</Font>
          <Button
            version={2}
            label={t('onboarding.modals.ShareholderDetails.openButton')}
            onClick={() =>
              updateModal({
                title: t('onboarding.modals.ShareholderDetails.title'),
                modalState: 'open',
                content: (
                  <ModalOwnershipDetailsForm
                    actionType='add'
                    action={action}
                    tableConfig={tableConfig}
                    postUrl={getEndpointUrl('CorpAOF/addshareholderdetails05')}
                  />
                ),
                removeFooter: true,
              })
            }
            disabled={checkMode('readOnly')}
          />
        </>
      }
      {
        formData.uboExists === 'true' && tableData?.length !== 0 && (
          <Table
            tableData={tableData}
            tableColumns={tableColumns}
            tableReadOnly={checkMode('readOnly')}
          />
        )
      }
      {!isLoading && (error || serverValidationError) && (
        <Popup type='error' label={error || serverValidationError} />
      )}
      {genericError.error && (
        <Popup
          removeSelfClose
          type='error'
          label={genericError.message}
          popupCloseHelperFunction={() => setGenericError({error: false, message: ''})}
          click={() => setGenericError({error: false, message: ''})}
        />
      )}
      {submittedSuccesifully && (
        <Popup
          removeSelfClose
          type='success'
          title={t('onboarding.success.title')}
          label={t('onboarding.success.label')}
          popupCloseHelperFunction={() => setSubmittedSuccesifully(false)}
          click={() => setSubmittedSuccesifully(false)}
        />
      )}
      <FormFooter noOfButtons={2}>
        <FormFooterActionsLeft>
          <Button
            label={t('onboarding.main.backButton')}
            version={2}
            disabled={props.disablePrev}
            onClick={props.prevStep}
          />
        </FormFooterActionsLeft>
        <FormFooterActionsRight>
          <Button
            label={props.isLastForm ? t('onboarding.main.saveAndSubmit') : t('onboarding.main.continueButton')}
            onClick={submitForm}
            disabled={
              (formData.uboExists === 'true' && tableData?.length === 0) ||
              formData.uboExists === ''
            }
          />
        </FormFooterActionsRight>
      </FormFooter>
    </FormStyle>
  )
}

export default CompanyOwnershipForm
